import { Link } from 'react-router';
import { Badge } from '@/components/Tag';
import { dateTime, num, qty } from '@/lib/format';
import { isOpen, statusOf, typeOf } from '../exceptions/status';
import type { ShipmentListItem } from './types';
import s from './ShipmentCard.module.css';

export const shipmentHref = (no: string) => `/shipments/${encodeURIComponent(no)}`;

export function sumLines(sh: ShipmentListItem) {
  const ship = sh.lines.reduce((a, l) => a + num(l.quantity), 0);
  const loss = sh.lines.reduce((a, l) => a + num(l.lossQuantity), 0);
  return { ship, loss, out: ship + loss };
}

/** 产品名去重：台账接口不带 orderLine，同一产品可能既是套装组件又是单品，这里只列名字不列套/个 */
export function productNames(sh: ShipmentListItem) {
  return [...new Set(sh.lines.map((l) => l.product.name))];
}

/** 异常徽标：未闭环红、已闭环灰（文档 18 §4.3） */
export function ExceptionBadge({ list, withText }: { list: ShipmentListItem['exceptions']; withText?: boolean }) {
  if (!list.length) return null;
  const open = list.filter(isOpen);
  const text = (open.length ? open : list).map((e) => `${typeOf(e.type).label} · ${statusOf(e.status).label}`).join('；');
  return (
    <>
      {open.length ? <Badge>{open.length} 个异常</Badge> : <Badge tone="grey">{list.length} 个异常 · 已闭环</Badge>}
      {withText && open.length > 0 && <span className={s.etext}>{text}</span>}
    </>
  );
}

/**
 * 台账卡（手机）：批次号 + 发货时间·出库人 / 客户·订单 / 物流 / 产品 / 数量；有异常才出提醒行。
 * 台账接口认不出套装，数量统一写「个」（套装组件按个累加），套数在详情里看。
 */
export function ShipmentCard({ sh }: { sh: ShipmentListItem }) {
  const t = sumLines(sh);
  const names = productNames(sh);
  return (
    <Link to={shipmentHref(sh.shipmentNo)} state={{ seed: sh }} className={s.card}>
      <div className={s.h}>
        <b className="tnum">{sh.shipmentNo}</b>
        <span className="tnum">{dateTime(sh.shippedAt)}{sh.createdBy && ` · ${sh.createdBy.name}`}</span>
      </div>
      <div className={s.c}>{sh.order.customer.name} <span className="tnum">· {sh.order.orderNo}</span></div>
      <div className={s.p}>{sh.logisticsCompany || '—'} · <span className="tnum">{sh.trackingNo || '—'}</span></div>
      <div className={s.l}>
        <span>{names.join('、') || '—'}</span>
        <b className="tnum">{sh.lines.length} 行</b>
      </div>
      <div className={s.d}>
        <span><i>发货</i><b>{qty(t.ship)}</b></span>
        {t.loss > 0 && <span><i>破损/补耗</i><b className="neg">{qty(t.loss)}</b></span>}
        <span className={s.out}><i>实际出库</i><b>{qty(t.out)}</b> 个</span>
      </div>
      {sh.exceptions.length > 0 && (
        <div className={s.f}><ExceptionBadge list={sh.exceptions} withText /></div>
      )}
    </Link>
  );
}
